import Storage from '@/modules/storage';

export const VERSION = '0.27.1';
export const SET_MODE = 'SET_MODE';
export const SET_DEBUG_MODE = 'SET_DEBUG_MODE';
export const SET_APP_READY = 'SET_APP_READY';
export const SET_SHOW_MENU = 'SET_SHOW_MENU';
export const SET_MENU_PAGE = 'SET_MENU_PAGE';
export const SET_SHOW_GARMENT = 'SET_SHOW_GARMENT';
export const SET_GARMENT_SOURCE = 'SET_GARMENT_SOURCE';
export const SET_SCANNING_QR = 'SET_SCANNING_QR';
export const SET_STORE_NAME = 'SET_STORE_NAME';
export const SET_STORE_ID = 'SET_STORE_ID';
export const SET_CHARGE_ON_USER = 'SET_CHARGE_ON_USER';
export const SET_MOBILE_SCAN_PRICE = 'SET_MOBILE_SCAN_PRICE';
export const SET_MEASURING_SYSTEM = 'SET_MEASURING_SYSTEM';
export const SET_EXIT_ON_SCAN_COMPLETE = 'SET_EXIT_ON_SCAN_COMPLETE';
export const SET_SCANNERS_CONFIGS = 'SET_SCANNERS_CONFIGS';

const app = {
  namespaced: true,
  state: () => ({
    version: VERSION,
    mode: '',
    debugMode: false,
    appReady: false,
    showMenu: false,
    menuPage: {},
    showGarment: false,
    garmentSource: '',
    isScanningQr: false,
    exitOnScanComplete: false,
    scannersConfigs: {},
    measuringSystem: Storage.getMeasuringSystem(),
    // never persisted: the mobile scanner credentials are read from the store
    // QR code each time and only live in the page that uses them
    storeSettings: {
      storeName: '',
      storeId: '',
      charge_on_user: false,
      mobile_scan_price: '',
    },
  }),
  mutations: {
    [SET_MODE](state, mode) {
      state.mode = mode || '';
    },
    [SET_DEBUG_MODE](state, enable) {
      state.debugMode = enable;
    },
    [SET_APP_READY](state, ready) {
      state.appReady = ready;
    },
    [SET_SHOW_MENU](state, show) {
      state.showMenu = show;
    },
    [SET_MENU_PAGE](state, page) {
      state.menuPage = page;
    },
    [SET_SHOW_GARMENT](state, show) {
      state.showGarment = show;
    },
    [SET_GARMENT_SOURCE](state, source) {
      state.garmentSource = source;
    },
    [SET_SCANNING_QR](state, scanning) {
      state.isScanningQr = scanning;
    },
    [SET_STORE_NAME](state, storeName) {
      state.storeSettings.storeName = storeName;
    },
    [SET_CHARGE_ON_USER](state, chargeOnUser) {
      state.storeSettings.charge_on_user = chargeOnUser;
    },
    [SET_STORE_ID](state, storeId) {
      state.storeSettings.storeId = storeId;
    },
    [SET_MOBILE_SCAN_PRICE](state, mobileScanPrice) {
      state.storeSettings.mobile_scan_price = mobileScanPrice;
    },
    [SET_MEASURING_SYSTEM](state, system) {
      state.measuringSystem = system;
    },
    [SET_EXIT_ON_SCAN_COMPLETE](state, value) {
      state.exitOnScanComplete = value;
    },
    [SET_SCANNERS_CONFIGS](state, config) {
      state.scannersConfigs = config;
    },
  },
  actions: {
    setAppReady(context, isReady) {
      context.commit(SET_APP_READY, isReady);
    },
    showMenu(context, page) {
      context.commit(SET_SHOW_MENU, true);
      if (page && page.pageName) {
        context.commit(SET_MENU_PAGE, page);
      } else {
        context.commit(SET_MENU_PAGE, {});
      }
    },
    hideMenu(context) {
      context.commit(SET_SHOW_MENU, false);
    },
    displayGarment(context, source) {
      context.commit(SET_SHOW_GARMENT, true);
      context.commit(SET_GARMENT_SOURCE, source);
    },
    toggleGarment(context, show) {
      context.commit(SET_SHOW_GARMENT, show);
    },
    closeGarment(context) {
      context.commit(SET_SHOW_GARMENT, false);
      context.commit(SET_GARMENT_SOURCE, '');
    },
    launchQrScan(context) {
      context.commit(SET_SCANNING_QR, true);
    },
    cancelQrScan(context) {
      context.commit(SET_SCANNING_QR, false);
    },
    setAppMode(context, mode) {
      context.commit(SET_MODE, mode);
    },
    setExitOnScanComplete(context, value) {
      context.commit(SET_EXIT_ON_SCAN_COMPLETE, value);
    },
    setStoreConfigs({ commit }, configs) {
      const {
        store_name: storeName = '',
        store_id: storeId = '',
        charge_on_user: chargeOnUser = false,
        mobile_scan_price: mobileScanPrice = '',
      } = configs;
      commit(SET_STORE_NAME, storeName);
      commit(SET_CHARGE_ON_USER, !!chargeOnUser);
      commit(SET_MOBILE_SCAN_PRICE, mobileScanPrice);
      commit(SET_STORE_ID, storeId);
    },
    clearStoreConfigs({ commit }) {
      commit(SET_STORE_NAME, '');
      commit(SET_CHARGE_ON_USER, false);
      commit(SET_MOBILE_SCAN_PRICE, '');
      commit(SET_STORE_ID, '');
    },
    useMetricSystem({ commit }) {
      commit(SET_MEASURING_SYSTEM, 'metric');
      Storage.setMeasuringSystem('metric');
    },
    useImperialSystem({ commit }) {
      commit(SET_MEASURING_SYSTEM, 'imperial');
      Storage.setMeasuringSystem('imperial');
    },
    restoreScannersConfigs({ dispatch }) {
      const configs = Storage.getScannersConfigs();
      if (configs) {
        console.log('cached scanners configs found');
        dispatch('setScannersConfigs', configs);
      } else {
        console.log('no cached scanners configs found');
      }
    },
    setScannersConfigs({ commit }, configs) {
      commit(SET_SCANNERS_CONFIGS, configs);
      Storage.setScannersConfigs(configs);
    },
    clearScannersConfigs({ commit }) {
      commit(SET_SCANNERS_CONFIGS, {});
      Storage.removeScannersConfigs();
    },
  },
  getters: {
    isUsingMetric: state => state.measuringSystem === 'metric',
    isStamp: state => state.mode.indexOf('stamp') > -1,
    isIncein: state => state.mode.indexOf('incein') > -1,
    isHM: state => state.mode === 'h&m',
    isDAF: state => state.mode === 'd+af',
    isDailyBelle: state => state.mode === 'dailybelle',
    isWeb: state => !state.appReady,
    storeName: state => state.storeSettings.storeName,
    chargeOnUser: state => state.storeSettings.charge_on_user,
    storeId: state => state.storeSettings.storeId,
    mobileScanPrice: state => state.storeSettings.mobile_scan_price,
    scannersConfigs: state => state.scannersConfigs,
  },
};

export default app;
